// ===========================================
// Clients — Page liste refondue (UX + courtage assurance)
// KPIs + QuickFilters chips + table enrichie (avatar, CA, dropdown actions)
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, Grid,
    message, DatePicker, Dropdown, Empty, List, Pagination, Spin, Tooltip, Modal,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
    TeamOutlined, DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    MoreOutlined, PhoneOutlined, UserOutlined, BankOutlined, RiseOutlined,
    ClearOutlined, AppstoreOutlined, QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { clientsService, agencesService } from '../../common/services';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatPhone, formatDate, formatMontant, formatMontantPdf } from '../../common/utils/format';
import { useDebounce, useReferentiel, useLatestFetch, useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES, PDF_FOOT_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import MobileFab from '../../components/mobileFab';
import { useHasPermission } from '../../components/can';

import MiniKpiCard from './components/MiniKpiCard';
import QuickFilters from './components/QuickFilters';
import ClientAvatar from './components/ClientAvatar';

import './clients.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const getClientName = (record) => {
    return record.type === 'entreprise'
        ? (record.raisonSociale || record.nom || '')
        : `${record.nom || ''} ${record.prenom || ''}`.trim();
};

// ─── Format de date en relatif (cohérent avec dashboard) ──────────────────
const formatRelative = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 0) return formatDate(date);

    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    if (diffMin < 1)    return "À l'instant";
    if (diffMin < 60)   return `Il y a ${diffMin} min`;
    if (diffHr  < 24)   return `Il y a ${diffHr} h`;
    if (diffDay < 30)   return diffDay === 1 ? 'Hier' : `Il y a ${diffDay} jours`;
    if (diffMonth < 12) return `Il y a ${diffMonth} mois`;
    return `Il y a ${diffYear} an${diffYear > 1 ? 's' : ''}`;
};

const Clients = () => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── Permissions (masquer les actions non autorisées) ─────────────
    const canUpdate = useHasPermission('clients:update');
    const canDelete = useHasPermission('clients:delete');

    // ─── État ─────────────────────────────────────────────────────────
    const [clients, setClients] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [filterAgence, setFilterAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [filterType, setFilterType] = useState('all'); // 'all' | 'particulier' | 'entreprise'
    const [filterDates, setFilterDates] = useState(null);
    const [quickFilter, setQuickFilter] = useState('all'); // 'all' | 'particuliers' | 'entreprises'

    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const guardedFetch = useLatestFetch();

    // ─── Charger les KPIs ─────────────────────────────────────────────
    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const res = await clientsService.getStats({ agence: filterAgence });
            setStats(res?.data || null);
        } catch (err) {
            console.error('Erreur stats clients:', err);
        } finally {
            setLoadingStats(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchStats(); }, [filterAgence]);

    // ─── Synchro quickFilter ↔ filterType ─────────────────────────────
    // Les chips quickFilter sont l'interface haut niveau, qui mettent à jour
    // les filtres techniques sous-jacents (filterType, filterDates, etc.)
    const applyQuickFilter = (q) => {
        setQuickFilter(q);
        if (q === 'particuliers') setFilterType('particulier');
        else if (q === 'entreprises') setFilterType('entreprise');
        else setFilterType('all');
    };

    // ─── Fetch clients avec filtres ───────────────────────────────────
    const fetchClients = async (page = 1, pageSize = 20) => {
        setLoading(true);
        const params = {
            page, pageSize, q: debouncedSearch, agence: filterAgence, type: filterType,
            start: filterDates?.[0]?.format('YYYY-MM-DD'),
            end: filterDates?.[1]?.format('YYYY-MM-DD'),
        };
        try {
            await guardedFetch(
                () => clientsService.getAll(params),
                (res) => {
                    const payload = res?.data || res || {};
                    const items = payload.items || payload || [];
                    setClients(Array.isArray(items) ? items : []);
                    setPagination((prev) => ({
                        ...prev,
                        current: page,
                        pageSize,
                        total: payload.total || items.length || 0,
                    }));
                },
                (error) => {
                    console.error('Erreur chargement clients:', error);
                    toast.error('Erreur lors du chargement des clients');
                },
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterAgence, filterType, filterDates]);

    // ─── Compteur de filtres actifs ───────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let count = 0;
        if (filterAgence !== 'all') count++;
        if (filterDates) count++;
        return count;
    }, [filterAgence, filterDates]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterAgence('all');
        setFilterType('all');
        setFilterDates(null);
        setQuickFilter('all');
    };

    // ─── Suppression optimiste ────────────────────────────────────────
    const handleDelete = async (id) => {
        const previous = clients;
        setClients((prev) => prev.filter((c) => c._id !== id));
        setPagination((prev) => ({ ...prev, total: Math.max(0, (prev.total || 0) - 1) }));
        try {
            await clientsService.delete(id);
            toast.success('Client supprimé avec succès');
            fetchStats(); // Réactualise les KPIs
        } catch (error) {
            setClients(previous);
            setPagination((prev) => ({ ...prev, total: (prev.total || 0) + 1 }));
            toast.error(error?.message || 'Erreur lors de la suppression');
        }
    };

    // ─── Export helpers (conservés tels quels) ────────────────────────
    const fetchAllForExport = async () => {
        const hide = message.loading("Préparation de l'export...", 0);
        try {
            // L'API plafonne `limit` à 100 → on boucle sur toutes les pages
            // pour récupérer la TOTALITÉ des clients (pas seulement la page courante).
            const baseParams = {
                q: debouncedSearch,
                agence: filterAgence, type: filterType,
                start: filterDates?.[0]?.format('YYYY-MM-DD'),
                end: filterDates?.[1]?.format('YYYY-MM-DD'),
            };
            const LIMIT = 100;
            let page = 1;
            let all = [];
            let total = Infinity;
            // garde-fou (50 pages = 5000 clients max) contre une boucle infinie
            while (all.length < total && page <= 50) {
                const res = await clientsService.getAll({ ...baseParams, page, limit: LIMIT });
                const payload = res?.data || res || {};
                const items = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
                total = Number.isFinite(payload.total) ? payload.total : items.length;
                all = all.concat(items);
                if (!items.length) break;
                page += 1;
            }
            hide();
            return all;
        } catch (e) {
            hide();
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const buildExportData = (list) => list.map((c) => ({
        code: c.code || '',
        nom: getClientName(c),
        telephone: formatPhone(c.telephone),
        type: c.type === 'entreprise' ? 'Entreprise' : 'Particulier',
        agence: c.agence?.nom || '',
        nbContrats: c.nbContrats || 0,
        totalCa: formatMontantPdf(c.totalCa || 0),
        totalCaNum: c.totalCa || 0,
        dateCreation: c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : '',
    }));

    const exportHeaders = ['Code', 'Nom / Raison sociale', 'Téléphone', 'Type', 'Agence', 'Inscrit le', 'Nb contrats', 'CA généré'];

    const handleExportPDF = async () => {
        const allClients = await fetchAllForExport();
        if (!allClients.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        const agenceNom = resolveAgenceLabel({ selected: filterAgence, agences, canSwitchAgence, userAgenceId });
        doc.setFontSize(12);
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text('LISTE DES CLIENTS', centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Période de filtre (si renseignée)
        let pdfStartY = 38;
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 38);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 38);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 38);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 38);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            pdfStartY = 44;
        }

        const data = buildExportData(allClients);
        const totNbContrats = data.reduce((s, d) => s + (d.nbContrats || 0), 0);
        const totCa = data.reduce((s, d) => s + (d.totalCaNum || 0), 0);
        autoTable(doc, {
            startY: pdfStartY,
            head: [exportHeaders],
            body: data.map((d) => [d.code, d.nom, d.telephone, d.type, d.agence, d.dateCreation, d.nbContrats, d.totalCa]),
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: PDF_HEAD_STYLES,
            footStyles: PDF_FOOT_STYLES,
            alternateRowStyles: { fillColor: false },
            columnStyles: { 6: { halign: 'right' }, 7: { halign: 'right' } },
            showFoot: 'lastPage',
            foot: [[
                { content: 'TOTAL', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: String(totNbContrats), styles: { halign: 'right', fontStyle: 'bold' } },
                { content: formatMontantPdf(totCa), styles: { halign: 'right', fontStyle: 'bold' } },
            ]],
        });

        addExporterFooter(doc, exporterName);
        doc.save(`clients_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const allClients = await fetchAllForExport();
        if (!allClients.length) return;
        const data = buildExportData(allClients);
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Clients');
        ws.columns = [
            { width: 18 }, { width: 30 }, { width: 18 }, { width: 14 }, { width: 22 }, { width: 18 }, { width: 12 }, { width: 16 },
        ];
        await addExcelLogoHeader(wb, ws);
        const titleRow = ws.addRow(['LISTE DES CLIENTS']);
        ws.mergeCells(titleRow.number, 1, titleRow.number, exportHeaders.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, exportHeaders.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: exportHeaders.length, exporterName, today });
        ws.addRow([]);
        const headerRow = ws.addRow(exportHeaders);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };
        });
        data.forEach((d) => {
            ws.addRow([d.code, d.nom, d.telephone, d.type, d.agence, d.dateCreation, d.nbContrats, d.totalCa]);
        });
        addExcelTotals(ws, { headerRow: headerRow.number, numericCols: [6, 7], labelCol: 1 });
        borderExcelTable(ws, headerRow.number, exportHeaders.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `clients_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf', label: 'Exporter en PDF', icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    // ─── Actions dropdown (hors "Détail" qui est un bouton dédié) ─────
    // On ne liste que les actions autorisées par les permissions de l'utilisateur.
    const getRowActionsMenu = (record) => ({
        items: [
            canUpdate && {
                key: 'edit',
                icon: <EditOutlined />,
                label: 'Modifier',
            },
            canUpdate && canDelete && { type: 'divider' },
            canDelete && {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: 'Supprimer',
                danger: true,
            },
        ].filter(Boolean),
        onClick: ({ key, domEvent }) => {
            domEvent?.stopPropagation?.();
            if (key === 'edit') {
                navigate(`/dashboard/nouveau-client?id=${record._id}`);
            } else if (key === 'delete') {
                if (window.confirm(`Supprimer le client "${getClientName(record)}" ?`)) {
                    handleDelete(record._id);
                }
            }
        },
    });

    // ─── Définition des colonnes du tableau ───────────────────────────
    const columns = useMemo(() => [
        {
            title: 'Client',
            key: 'client',
            sorter: (a, b) => getClientName(a).localeCompare(getClientName(b)),
            render: (_, record) => (
                <Tooltip title={record.code || ''} placement="topLeft">
                    <div className="client-cell">
                        <ClientAvatar client={record} size={36} />
                        <div className="client-cell-body">
                            <div className="client-cell-name">{getClientName(record) || '—'}</div>
                        </div>
                    </div>
                </Tooltip>
            ),
            width: 280,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            filters: [
                { text: 'Particulier', value: 'particulier' },
                { text: 'Entreprise', value: 'entreprise' },
            ],
            render: (type) => (
                <Tag color={type === 'entreprise' ? 'blue' : 'green'}>
                    {type === 'entreprise' ? 'Entreprise' : 'Particulier'}
                </Tag>
            ),
            responsive: ['md'],
        },
        {
            title: 'Téléphone',
            dataIndex: 'telephone',
            key: 'telephone',
            render: (tel) => (
                <span className="client-cell-phone">
                    <PhoneOutlined /> {formatPhone(tel)}
                </span>
            ),
            width: 160,
            responsive: ['md'],
        },
        {
            title: 'Agence',
            key: 'agence',
            render: (_, record) => record.agence?.nom || '—',
            width: 160,
            responsive: ['lg'],
            ellipsis: true,
        },
        {
            title: 'Contrats',
            dataIndex: 'nbContrats',
            key: 'nbContrats',
            sorter: (a, b) => (a.nbContrats || 0) - (b.nbContrats || 0),
            align: 'center',
            width: 100,
            render: (val) => (
                <span className={`client-cell-nb ${val > 0 ? 'has-contrats' : ''}`}>
                    {val || 0}
                </span>
            ),
        },
        {
            title: 'CA généré',
            dataIndex: 'totalCa',
            key: 'totalCa',
            sorter: (a, b) => (a.totalCa || 0) - (b.totalCa || 0),
            align: 'right',
            width: 130,
            render: (val) => (
                <span className="client-cell-ca">
                    {formatMontant(val || 0)}
                </span>
            ),
            responsive: ['lg'],
        },
        {
            title: 'Créé',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            width: 130,
            render: (val) => (
                <Tooltip title={val ? formatDate(val) : '—'}>
                    <span className="client-cell-date">{formatRelative(val)}</span>
                </Tooltip>
            ),
            responsive: ['md'],
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <div className="client-cell-actions">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        className="btn-detail client-action-detail"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/client/${record._id}`);
                        }}
                    >
                        Détail
                    </Button>
                    {(canUpdate || canDelete) && (
                        <Dropdown
                            menu={getRowActionsMenu(record)}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Plus d'actions pour ${getClientName(record)}`}
                                className="client-action-more"
                            />
                        </Dropdown>
                    )}
                </div>
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [navigate, canUpdate, canDelete]);

    // ─── Options des QuickFilters ─────────────────────────────────────
    const quickFilterOptions = [
        { value: 'all', label: 'Tous', icon: <AppstoreOutlined />, count: stats?.total },
        { value: 'particuliers', label: 'Particuliers', icon: <UserOutlined />, count: stats?.particuliers?.count },
        { value: 'entreprises', label: 'Entreprises', icon: <BankOutlined />, count: stats?.entreprises?.count },
    ];

    return (
        <div className="clients-page">
            {/* Header : titre + actions */}
            <header className="clients-header">
                <div className="clients-header-text">
                    <h1 className="clients-title">
                        <TeamOutlined /> Clients
                    </h1>
                    <p className="clients-subtitle">Particuliers et entreprises de votre portefeuille</p>
                </div>
                <div className="clients-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/nouveau-client')}
                        className="clients-btn-new"
                    >
                        {!isMobile && 'Nouveau client'}
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Gérer les clients</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page liste tous les clients de votre portefeuille (particuliers et
                    entreprises) et permet de les gérer.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <TeamOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Indicateurs</strong> — En haut : nombre total de
                                    clients, particuliers, entreprises et chiffre d'affaires. Ils se
                                    recalculent selon les filtres et l'agence sélectionnée.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SearchOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Rechercher &amp; filtrer</strong> — Recherchez par nom
                                    ou téléphone, puis filtrez par type (Particulier / Entreprise),
                                    période et agence. Les onglets rapides affinent la liste.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <EyeOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Actions par ligne</strong> — « Détail » ouvre la fiche
                                    complète ; le menu <strong>⋮</strong> permet de
                                    <strong> modifier</strong> ou <strong>supprimer</strong> le client.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de la liste filtrée.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <PlusOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Nouveau client</strong> — Le bouton « Nouveau client »
                                    crée une fiche. Un client doit exister avant de pouvoir lui créer
                                    un contrat.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs (4 mini-cartes) */}
            <section className="clients-kpis" aria-label="Indicateurs clients">
                <MiniKpiCard
                    icon={<TeamOutlined />}
                    label="Total clients"
                    value={stats?.total}
                    tone="blue"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<UserOutlined />}
                    label="Particuliers"
                    value={stats?.particuliers?.count}
                    suffix={stats?.particuliers?.pct !== undefined ? `(${stats.particuliers.pct}%)` : ''}
                    tone="green"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<BankOutlined />}
                    label="Entreprises"
                    value={stats?.entreprises?.count}
                    suffix={stats?.entreprises?.pct !== undefined ? `(${stats.entreprises.pct}%)` : ''}
                    tone="indigo"
                    loading={loadingStats}
                />
                <MiniKpiCard
                    icon={<RiseOutlined />}
                    label="Nouveaux 30j"
                    value={stats?.nouveaux30j?.count}
                    delta={stats?.nouveaux30j?.delta}
                    tone="teal"
                    loading={loadingStats}
                />
            </section>

            {/* Carte principale : toolbar inline + table */}
            <Card className="clients-main-card">
                {/* Toolbar : recherche + quick filters + agence + période + effacer */}
                <div className="clients-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher par nom, téléphone, code, email..."
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="clients-search"
                        aria-label="Rechercher un client"
                    />

                    <QuickFilters
                        value={quickFilter}
                        onChange={applyQuickFilter}
                        options={quickFilterOptions}
                    />

                    {canSwitchAgence && (
                        <Select
                            value={filterAgence}
                            onChange={setFilterAgence}
                            className="clients-filter-agence"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((ag) => (
                                <Option key={ag._id} value={ag._id}>{ag.nom}</Option>
                            ))}
                        </Select>
                    )}

                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Date début', 'Date fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="clients-filter-dates"
                        allowClear
                    />

                    {(activeFiltersCount > 0 || search || quickFilter !== 'all') && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="clients-btn-clear"
                            aria-label="Effacer tous les filtres"
                        >
                            Effacer
                        </Button>
                    )}

                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="clients-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                {/* Liste : table sur desktop, cards sur mobile */}
                {isMobile ? (
                    <Spin spinning={loading}>
                        {loading ? (
                            <div style={{ minHeight: 200 }} />
                        ) : clients.length === 0 ? (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Aucun client trouvé"
                                style={{ padding: 32 }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => navigate('/dashboard/nouveau-client')}
                                    className="clients-btn-new"
                                >
                                    Ajouter un client
                                </Button>
                            </Empty>
                        ) : (
                            <List
                                dataSource={clients}
                                rowKey="_id"
                                split={false}
                                renderItem={(record) => (
                                    <Card
                                        key={record._id}
                                        size="small"
                                        className={`clients-mobile-card client-${record.type || 'particulier'}`}
                                        onClick={() => navigate(`/dashboard/client/${record._id}`)}
                                    >
                                        <div className="mobile-card-row">
                                            <ClientAvatar client={record} size={44} />
                                            <div className="mobile-card-body">
                                                                <div className="mobile-card-name">
                                                    {getClientName(record) || '—'}
                                                </div>
                                                <div className="mobile-card-phone">
                                                    <PhoneOutlined /> {formatPhone(record.telephone)}
                                                </div>
                                                <div className="mobile-card-tags">
                                                    <Tag color={record.type === 'entreprise' ? 'blue' : 'green'}>
                                                        {record.type === 'entreprise' ? 'Entreprise' : 'Particulier'}
                                                    </Tag>
                                                    <Tag>{record.nbContrats || 0} contrat{record.nbContrats > 1 ? 's' : ''}</Tag>
                                                    {record.totalCa > 0 && (
                                                        <Tag color="purple">{formatMontant(record.totalCa)}</Tag>
                                                    )}
                                                </div>
                                            </div>
                                            <Dropdown menu={getRowActionsMenu(record)} trigger={['click']}>
                                                <Button
                                                    type="text"
                                                    icon={<MoreOutlined />}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label="Actions"
                                                />
                                            </Dropdown>
                                        </div>
                                    </Card>
                                )}
                            />
                        )}
                        <div className="clients-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchClients(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={clients}
                        columns={columns}
                        rowKey="_id"
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        pagination={{
                            ...pagination,
                            onChange: (page, pageSize) => fetchClients(page, pageSize),
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} client${total > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/dashboard/client/${record._id}`),
                            style: { cursor: 'pointer' },
                        })}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        activeFiltersCount > 0 || search
                                            ? 'Aucun client ne correspond aux filtres'
                                            : 'Aucun client trouvé'
                                    }
                                >
                                    {(activeFiltersCount > 0 || search) ? (
                                        <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
                                            Effacer les filtres
                                        </Button>
                                    ) : (
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => navigate('/dashboard/nouveau-client')}
                                            className="clients-btn-new"
                                        >
                                            Ajouter un client
                                        </Button>
                                    )}
                                </Empty>
                            ),
                        }}
                    />
                )}
            </Card>

            <MobileFab
                icon={<PlusOutlined />}
                onClick={() => navigate('/dashboard/nouveau-client')}
                title="Nouveau client"
            />
        </div>
    );
};

export default Clients;
